<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { Card } from '$lib/api/types';
  import CardWrapper from '$components/cards/CardWrapper.svelte';

  export let cards: Card[];
  export let isOwnPage: boolean;

  // All cards including sidebar links (for collision detection when dropping from sidebar)
  export let allCards: Card[] = [];

  // Sidebar link currently being dragged (passed from parent)
  export let draggingSidebarLink: Card | null = null;

  const dispatch = createEventDispatcher<{
    cardUpdated: Card;
    cardDeleted: string;
    cardMoved: { cardId: string; x: number; y: number; pushedCards: Array<{ cardId: string; y: number }> };
    cardResized: { cardId: string; width: number; height: number; pushedCards: Array<{ cardId: string; y: number }> };
    cardsCompacted: Array<{ cardId: string; y: number }>;
    sidebarLinkDropped: { cardId: string; x: number; y: number; pushedCards: Array<{ cardId: string; y: number }> };
    cardDragStart: Card;
    cardDragEnd: void;
  }>();

  // Grid dimensions (internal units)
  const GRID_COLS = 8;
  const GRID_ROWS = 12; // Enough rows for scrolling

  let draggingCard: Card | null = null;
  let dragOffset: { x: number; y: number } = { x: 0, y: 0 }; // Offset within card where drag started
  let dragOverCell: { x: number; y: number } | null = null;
  let previewPushedCards: Array<{ cardId: string; y: number }> = []; // Cards that will be pushed
  let gridElement: HTMLElement;

  // Local copy of sidebar link being dragged (preserved until drop completes)
  let localDraggingSidebarLink: Card | null = null;

  // Update local copy when prop changes, but don't clear immediately
  $: if (draggingSidebarLink) {
    localDraggingSidebarLink = draggingSidebarLink;
  }

  function handleCardUpdated(event: CustomEvent<Card>) {
    dispatch('cardUpdated', event.detail);
  }

  function handleCardDeleted(event: CustomEvent<string>) {
    dispatch('cardDeleted', event.detail);
  }

  // Check if a cell is occupied by any card
  function isCellOccupied(x: number, y: number, excludeCardId?: string): boolean {
    return cards.some(card => {
      if (excludeCardId && card.id === excludeCardId) return false;
      return (
        x >= card.x &&
        x < card.x + card.width &&
        y >= card.y &&
        y < card.y + card.height
      );
    });
  }

  // Check if a card can be placed at position
  function canPlaceCard(x: number, y: number, width: number, height: number, excludeCardId?: string): boolean {
    // Check bounds
    if (x < 0 || y < 0 || x + width > GRID_COLS) return false;

    // Check each cell the card would occupy
    for (let dx = 0; dx < width; dx++) {
      for (let dy = 0; dy < height; dy++) {
        if (isCellOccupied(x + dx, y + dy, excludeCardId)) {
          return false;
        }
      }
    }
    return true;
  }

  // Get grid cell from mouse position, accounting for drag offset
  function getCellFromEvent(event: DragEvent): { x: number; y: number } | null {
    if (!gridElement) return null;

    const rect = gridElement.getBoundingClientRect();
    const cellWidth = rect.width / GRID_COLS;
    const cellHeight = 88; // Match grid-auto-rows
    const gap = 20; // Match grid gap

    // Calculate cell position, subtracting the drag offset to get the card's top-left corner
    const mouseX = event.clientX - rect.left - (dragOffset.x * (cellWidth + gap));
    const mouseY = event.clientY - rect.top + gridElement.scrollTop - (dragOffset.y * (cellHeight + gap));

    const x = Math.floor(mouseX / (cellWidth + gap) + 0.5); // +0.5 for better snapping
    const y = Math.floor(mouseY / (cellHeight + gap) + 0.5);

    if (x < 0 || y < 0) return null;
    return { x: Math.max(0, x), y: Math.max(0, y) };
  }

  function handleDragStart(event: DragEvent, card: Card) {
    if (!isOwnPage) return;
    draggingCard = card;
    // Initialize with current position
    dragOverCell = { x: card.x, y: card.y };

    // Calculate offset within the card where drag started (in grid units)
    const target = event.currentTarget as HTMLElement;
    if (target && gridElement) {
      const cardRect = target.getBoundingClientRect();
      const gridRect = gridElement.getBoundingClientRect();
      const cellWidth = gridRect.width / GRID_COLS;
      const cellHeight = 88;
      const gap = 20;

      // How many cells from the card's top-left corner did the user click?
      const offsetX = (event.clientX - cardRect.left) / (cellWidth + gap);
      const offsetY = (event.clientY - cardRect.top) / (cellHeight + gap);
      dragOffset = { x: Math.min(offsetX, card.width - 1), y: Math.min(offsetY, card.height - 1) };
    }

    event.dataTransfer?.setData('text/plain', card.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
    dispatch('cardDragStart', card);
  }

  function handleDragEnd() {
    draggingCard = null;
    dragOverCell = null;
    dragOffset = { x: 0, y: 0 };
    previewPushedCards = [];
    dispatch('cardDragEnd');
  }

  function handleGridDragOver(event: DragEvent) {
    if (!isOwnPage) return;
    if (!draggingCard && !localDraggingSidebarLink) return;
    event.preventDefault();

    const targetCard = draggingCard || localDraggingSidebarLink;
    if (!targetCard) return;

    const cell = getCellFromEvent(event);
    if (cell) {
      // Clamp x to valid range for card width
      const x = Math.max(0, Math.min(cell.x, GRID_COLS - targetCard.width));
      const y = Math.max(0, cell.y);
      // Only update if position actually changed
      if (!dragOverCell || dragOverCell.x !== x || dragOverCell.y !== y) {
        dragOverCell = { x, y };
        // Calculate preview of pushed cards
        const movedCard = { ...targetCard, x, y };
        previewPushedCards = calculatePushedCards(movedCard, targetCard.id);
      }
    }
  }

  function handleGridDragLeave(event: DragEvent) {
    // Only clear if leaving the grid entirely
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!gridElement?.contains(relatedTarget)) {
      localDraggingSidebarLink = null;
      dragOverCell = null;
      previewPushedCards = [];
    }
  }

  function handleGridDrop(event: DragEvent) {
    if (!isOwnPage || !dragOverCell) return;
    event.preventDefault();

    // Check if this is a sidebar link being dropped
    if (localDraggingSidebarLink) {
      // Save values before clearing state
      const cardId = localDraggingSidebarLink.id;
      const x = dragOverCell.x;
      const y = dragOverCell.y;
      const pushedCards = [...previewPushedCards];

      // Clear state BEFORE dispatching to avoid stale state during re-render
      localDraggingSidebarLink = null;
      dragOverCell = null;
      previewPushedCards = [];

      // Sidebar link dropped into grid
      dispatch('sidebarLinkDropped', { cardId, x, y, pushedCards });
      return;
    }

    if (!draggingCard) {
      dragOverCell = null;
      previewPushedCards = [];
      return;
    }

    // Skip if position didn't change
    if (draggingCard.x === dragOverCell.x && draggingCard.y === dragOverCell.y) {
      draggingCard = null;
      dragOverCell = null;
      dragOffset = { x: 0, y: 0 };
      previewPushedCards = [];
      return;
    }

    // Use already calculated pushed cards from preview
    const pushedCards = previewPushedCards;

    dispatch('cardMoved', {
      cardId: draggingCard.id,
      x: dragOverCell.x,
      y: dragOverCell.y,
      pushedCards,
    });

    draggingCard = null;
    dragOverCell = null;
    dragOffset = { x: 0, y: 0 };
    previewPushedCards = [];
  }

  // Calculate which cards need to be pushed down
  function calculatePushedCards(
    newCard: { x: number; y: number; width: number; height: number },
    excludeCardId: string
  ): Array<{ cardId: string; y: number }> {
    const pushedCards: Array<{ cardId: string; y: number }> = [];
    const cardPositions = new Map<string, number>();

    // Initialize with current positions
    cards.forEach(c => {
      if (c.id !== excludeCardId) {
        cardPositions.set(c.id, c.y);
      }
    });

    let hasChanges = true;
    while (hasChanges) {
      hasChanges = false;

      for (const card of cards) {
        if (card.id === excludeCardId) continue;

        const currentY = cardPositions.get(card.id) || card.y;
        const virtualCard = { ...card, y: currentY };

        // Check overlap with moved/resized card
        if (cardsOverlap(newCard, virtualCard)) {
          const newY = newCard.y + newCard.height;
          if (currentY < newY) {
            cardPositions.set(card.id, newY);
            hasChanges = true;
          }
        }

        // Check overlap with other pushed cards
        for (const [otherId, otherY] of cardPositions) {
          if (otherId === card.id) continue;
          const otherCard = cards.find(c => c.id === otherId);
          if (!otherCard) continue;

          const virtualOther = { ...otherCard, y: otherY };
          if (cardsOverlap(virtualOther, virtualCard)) {
            const newY = virtualOther.y + virtualOther.height;
            if (currentY < newY) {
              cardPositions.set(card.id, newY);
              hasChanges = true;
            }
          }
        }
      }
    }

    // Collect cards that were pushed
    for (const [cardId, newY] of cardPositions) {
      const originalCard = cards.find(c => c.id === cardId);
      if (originalCard && originalCard.y !== newY) {
        pushedCards.push({ cardId, y: newY });
      }
    }

    return pushedCards;
  }

  // Check if two cards overlap
  function cardsOverlap(
    card1: { x: number; y: number; width: number; height: number },
    card2: { x: number; y: number; width: number; height: number }
  ): boolean {
    return !(
      card1.x + card1.width <= card2.x ||
      card2.x + card2.width <= card1.x ||
      card1.y + card1.height <= card2.y ||
      card2.y + card2.height <= card1.y
    );
  }

  // Compact cards upward - move each card as high as possible without overlapping
  // Cards cannot move above section cards that were originally above them
  function compactCards(): Array<{ cardId: string; y: number }> {
    const movedCards: Array<{ cardId: string; y: number }> = [];

    // Sort cards by y position (top to bottom)
    const sortedCardsForCompact = [...cards].sort((a, b) => a.y - b.y);
    const newPositions = new Map<string, number>();

    // Initialize with current positions
    sortedCardsForCompact.forEach(c => newPositions.set(c.id, c.y));

    // Find section cards and their positions
    const sectionCards = cards.filter(c => c.type === 'section');

    for (const card of sortedCardsForCompact) {
      // Find the minimum Y this card can go to (respecting section boundaries)
      // Card cannot move above any section that was originally above it
      let minY = 0;
      for (const section of sectionCards) {
        const sectionY = newPositions.get(section.id) ?? section.y;
        // If section is above this card's original position, card cannot go above it
        if (section.y < card.y) {
          minY = Math.max(minY, sectionY + section.height);
        }
      }

      // Try to move this card up as high as possible (but not above minY)
      let bestY = minY;

      // Check each row from minY to find the highest valid position
      for (let tryY = minY; tryY <= card.y; tryY++) {
        const virtualCard = { ...card, y: tryY };
        let canPlace = true;

        // Check overlap with cards above (already positioned)
        for (const otherCard of sortedCardsForCompact) {
          if (otherCard.id === card.id) continue;

          const otherY = newPositions.get(otherCard.id) ?? otherCard.y;
          const virtualOther = { ...otherCard, y: otherY };

          if (cardsOverlap(virtualCard, virtualOther)) {
            canPlace = false;
            break;
          }
        }

        if (canPlace) {
          bestY = tryY;
          break;
        }
      }

      newPositions.set(card.id, bestY);

      if (bestY !== card.y) {
        movedCards.push({ cardId: card.id, y: bestY });
      }
    }

    return movedCards;
  }

  // Trigger compaction and dispatch event
  export function triggerCompact() {
    const movedCards = compactCards();
    if (movedCards.length > 0) {
      dispatch('cardsCompacted', movedCards);
    }
  }

  // Handle card resize with collision detection and push
  function handleCardResized(event: CustomEvent<{ cardId: string; width: number; height: number }>) {
    const { cardId, width, height } = event.detail;
    const resizedCard = cards.find(c => c.id === cardId);
    if (!resizedCard) return;

    // Create a virtual representation of the resized card
    const newCard = { ...resizedCard, width, height };

    // Find all cards that would overlap with the resized card
    const pushedCards: Array<{ cardId: string; y: number }> = [];
    let cardsToCheck = cards.filter(c => c.id !== cardId);
    let hasChanges = true;

    // Iteratively push cards down until no more overlaps
    while (hasChanges) {
      hasChanges = false;

      for (const card of cardsToCheck) {
        // Check overlap with resized card
        if (cardsOverlap(newCard, card)) {
          // Push this card down below the resized card
          const newY = newCard.y + newCard.height;
          if (card.y !== newY) {
            const existing = pushedCards.find(p => p.cardId === card.id);
            if (existing) {
              existing.y = Math.max(existing.y, newY);
            } else {
              pushedCards.push({ cardId: card.id, y: newY });
            }
            card.y = newY;
            hasChanges = true;
          }
        }

        // Check overlap with other pushed cards
        for (const pushed of pushedCards) {
          const pushedCard = cards.find(c => c.id === pushed.cardId);
          if (pushedCard && card.id !== pushed.cardId) {
            const virtualPushed = { ...pushedCard, y: pushed.y };
            if (cardsOverlap(virtualPushed, card)) {
              const newY = virtualPushed.y + virtualPushed.height;
              if (card.y !== newY) {
                const existing = pushedCards.find(p => p.cardId === card.id);
                if (existing) {
                  existing.y = Math.max(existing.y, newY);
                } else {
                  pushedCards.push({ cardId: card.id, y: newY });
                }
                card.y = newY;
                hasChanges = true;
              }
            }
          }
        }
      }
    }

    dispatch('cardResized', { cardId, width, height, pushedCards });
  }


  // Calculate max row needed based on cards
  $: maxRow = Math.max(
    GRID_ROWS,
    ...cards.map(card => card.y + card.height + 2)
  );

  // Sort cards by y then x for proper mobile auto-flow
  $: sortedCards = [...cards].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });

  // Group cards by sections for mobile layout
  // Each group: { section: Card | null, cards: Card[] }
  interface CardGroup { section: Card | null; cards: Card[] }
  $: cardGroups = sortedCards.reduce<CardGroup[]>((groups, card) => {
    if (card.type === 'section') {
      // Start a new group with this section
      groups.push({ section: card, cards: [] });
    } else {
      // Add to current group (or create initial group if none exists)
      if (groups.length === 0) {
        groups.push({ section: null, cards: [] });
      }
      groups[groups.length - 1].cards.push(card);
    }
    return groups;
  }, []);
</script>

<div
  class="card-grid"
  bind:this={gridElement}
  on:dragover={handleGridDragOver}
  on:dragleave={handleGridDragLeave}
  on:drop={handleGridDrop}
  style="grid-template-rows: repeat({maxRow}, 88px);"
  role="grid"
>
  <!-- Drop indicator -->
  {#if isOwnPage && (draggingCard || localDraggingSidebarLink) && dragOverCell}
    {@const targetCard = draggingCard || localDraggingSidebarLink}
    <div
      class="bg-blue-200/50 border-2 border-dashed border-blue-400 rounded-2xl pointer-events-none"
      style="
        grid-column: {dragOverCell.x + 1} / span {targetCard?.width || 2};
        grid-row: {dragOverCell.y + 1} / span {targetCard?.height || 2};
      "
    />
  {/if}

  {#each cardGroups as group, groupIndex (group.section?.id ?? `group-${groupIndex}`)}
    <!-- Section card (if exists) -->
    {#if group.section}
      {@const pushedPosition = previewPushedCards.find(p => p.cardId === group.section?.id)}
      <CardWrapper
        card={group.section}
        editable={isOwnPage}
        isDragging={draggingCard?.id === group.section.id}
        overrideY={pushedPosition?.y}
        on:updated={handleCardUpdated}
        on:deleted={handleCardDeleted}
        on:dragstart={(e) => handleDragStart(e.detail, group.section)}
        on:dragend={handleDragEnd}
        on:resized={handleCardResized}
      />
    {/if}
    <!-- Cards in this group wrapped in a container -->
    <div class="card-group">
      {#each group.cards as card (card.id)}
        {@const pushedPosition = previewPushedCards.find(p => p.cardId === card.id)}
        <CardWrapper
          {card}
          editable={isOwnPage}
          isDragging={draggingCard?.id === card.id}
          overrideY={pushedPosition?.y}
          on:updated={handleCardUpdated}
          on:deleted={handleCardDeleted}
          on:dragstart={(e) => handleDragStart(e.detail, card)}
          on:dragend={handleDragEnd}
          on:resized={handleCardResized}
        />
      {/each}
    </div>
  {/each}

  {#if cards.length === 0 && isOwnPage}
    <div class="col-span-full text-center py-16 text-gray-400">
      <p class="text-lg mb-2">まだカードがありません</p>
      <p class="text-sm">下のツールバーからカードを追加してください</p>
    </div>
  {/if}
</div>
